import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Clock, MapPin } from "lucide-react";
import api from "@/lib/api";
import { usePageMeta } from "@/lib/seo";
import { F, PayBadge, Devices, CShell } from "@/components/careers/ui";
import { ApplyForm } from "@/components/careers/ApplyForm";

const List = ({ title, items }) => !items?.length ? null : (
  <div><p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">{title}</p>
    <ul className="space-y-2">{items.map((x, i) => <li key={i} className="flex gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />{x}</li>)}</ul></div>
);

export default function JobPage() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const invite = sp.get("invite") || "";
  const [j, setJ] = useState(null);
  const [err, setErr] = useState("");
  usePageMeta(j ? `${j.title} — Remote Job` : "Remote Job", j?.summary || "");
  useEffect(() => { api.get(`/api/careers/jobs/${slug}`, { params: { invite } }).then(r => setJ(r.data)).catch(() => setErr("This job could not be found.")); }, [slug, invite]);
  if (err) return <CShell testId="job-page"><p className="text-slate-600" data-testid="job-not-found">{err} <Link to="/careers" className="text-blue-700 font-semibold">See all jobs</Link></p></CShell>;
  if (!j) return <CShell testId="job-page"><p className="text-slate-400">Loading…</p></CShell>;
  return (
    <CShell testId="job-page">
      <Link to="/careers" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="job-back"><ArrowLeft className="w-4 h-4" />All jobs</Link>
      <div className="grid lg:grid-cols-[1fr_440px] gap-8 items-start">
        <div className="space-y-6">
          <div className="space-y-3"><PayBadge j={j} />
            <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight" style={F} data-testid="job-title">{j.title}</h1>
            <p className="text-base text-slate-600">{j.summary}</p>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600"><b className="text-slate-900" data-testid="job-pay">{j.pay_text}</b><span className="flex items-center gap-1"><Clock className="w-4 h-4" />{j.hours}</span><span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{j.location}</span></div>
            <Devices list={j.devices} />
            {j.status === "open" && <a href="#apply" className="lg:hidden inline-block h-11 px-6 leading-[2.75rem] rounded-full bg-amber-500 text-slate-900 font-bold" data-testid="job-apply-jump">Apply now</a>}
          </div>
          {j.status !== "open" && <p className="bg-slate-200 rounded-xl p-3 text-sm font-semibold" data-testid="job-closed">This position is closed to new applications.</p>}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-6"><List title="What you'll do" items={j.responsibilities} /><List title="What you need" items={j.requirements} /><List title="Perks" items={j.perks} /></div>
        </div>
        {j.status === "open" && <ApplyForm job={j} invite={invite} />}
      </div>
    </CShell>
  );
}
